import { useCallback, useEffect, useMemo, useState } from 'react'
import { Search, MapPin, MapPinned, Building2, Stethoscope, FlaskConical, Plus, Minus, Loader2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Badge } from '@/components/ui/Badge'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { type PageSize } from '@/hooks/useCrudList'
import type { PaginationMeta } from '@/types/api'
import { normalizeForMatch } from '@/lib/validators'
import { toast } from '@/components/ui/Toast'
import { CredentialingProvidersService } from '@/services/modules/CredentialingService'
import { LookupService, type State, type City } from '@/services/modules/LookupService'
import { BrazilMap } from './BrazilMap'

interface MapRow { id: string; name: string; abbreviation: string | null; providers: number; specialtiesCount: number; testsCount: number }
type CitySpecialty = { id: string; specialty: string; providers: number; providerNames: string[] }
type CityDetail = { loading: boolean; items: CitySpecialty[]; error?: boolean }

export function CredentialingReportMapPage() {
  const [states, setStates] = useState<State[]>([])
  const [stateCounts, setStateCounts] = useState<Record<string, number>>({})  // UF -> credenciados
  const [loadingMap, setLoadingMap] = useState(false)

  // estado selecionado (drill-down) → lista de cidades
  const [selectedUf, setSelectedUf] = useState<string | null>(null)
  const [selectedStateName, setSelectedStateName] = useState('')
  const [cities, setCities] = useState<City[]>([])
  const [cityRows, setCityRows] = useState<MapRow[]>([])
  const [loadingCities, setLoadingCities] = useState(false)

  // "+" da cidade: especialidades atendidas ali (carregadas uma vez por cidade).
  const [openCities, setOpenCities] = useState<string[]>([])
  const [cityDetails, setCityDetails] = useState<Record<string, CityDetail>>({})
  // Cidade grande (São Paulo tem ~900 especialidades): busca e "ver todas".
  const [cityQuery, setCityQuery] = useState<Record<string, string>>({})
  const [cityShowAll, setCityShowAll] = useState<string[]>([])

  const [cityFilter, setCityFilter] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<PageSize>(25)

  // carga inicial: estados (id/UF/nome) + agregação por estado para colorir o mapa
  useEffect(() => {
    LookupService.states().then(setStates).catch(() => setStates([]))
    setLoadingMap(true)
    CredentialingProvidersService.reportMap()
      .then(res => {
        const counts: Record<string, number> = {}
        for (const r of res.data) if (r.abbreviation) counts[r.abbreviation] = r.providers
        setStateCounts(counts)
      })
      .catch(() => toast.error('Erro ao carregar o mapa.'))
      .finally(() => setLoadingMap(false))
  }, [])

  const selectState = useCallback((uf: string, stateId: string, name: string) => {
    setSelectedUf(uf)
    setSelectedStateName(`${name} (${uf})`)
    setCityFilter(''); setSearch(''); setPage(1)
    setOpenCities([]); setCityDetails({}); setCityQuery({}); setCityShowAll([])
    setLoadingCities(true)
    LookupService.cities(stateId).then(setCities).catch(() => setCities([]))
    CredentialingProvidersService.reportMap(stateId)
      .then(res => setCityRows(res.data))
      .catch(() => toast.error('Erro ao carregar as cidades.'))
      .finally(() => setLoadingCities(false))
  }, [])

  function toggleCity(row: MapRow) {
    if (openCities.includes(row.id)) { setOpenCities(ids => ids.filter(id => id !== row.id)); return }
    setOpenCities(ids => [...ids, row.id])
    if (cityDetails[row.id] && !cityDetails[row.id].error) return
    setCityDetails(d => ({ ...d, [row.id]: { loading: true, items: [] } }))
    CredentialingProvidersService.reportMapCitySpecialties(row.id)
      .then(items => setCityDetails(d => ({ ...d, [row.id]: { loading: false, items } })))
      .catch(() => {
        setCityDetails(d => ({ ...d, [row.id]: { loading: false, items: [], error: true } }))
        toast.error('Erro ao carregar as especialidades da cidade.')
      })
  }

  function renderCityDetail(row: MapRow) {
    if (!openCities.includes(row.id)) return null
    const det = cityDetails[row.id]
    if (!det || det.loading) {
      return <p className="flex items-center gap-2 text-xs text-gray-500"><Loader2 size={14} className="animate-spin" /> Carregando especialidades</p>
    }
    if (det.error) return <p className="text-xs text-rose-600">Não foi possível carregar as especialidades.</p>
    if (det.items.length === 0) return <p className="text-xs text-gray-500">Nenhuma especialidade cadastrada nos credenciados desta cidade.</p>
    const LIMITE = 40
    const q = normalizeForMatch(cityQuery[row.id] ?? '')
    const achadas = q ? det.items.filter(it => normalizeForMatch(it.specialty).includes(q)) : det.items
    const todas = cityShowAll.includes(row.id) || !!q
    const visiveis = todas ? achadas : achadas.slice(0, LIMITE)
    return (
      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-medium text-gray-600">
            {det.items.length} especialidade{det.items.length > 1 ? 's' : ''} em {row.name} · número de credenciados que atendem cada uma
          </p>
          {det.items.length > LIMITE && (
            <Input value={cityQuery[row.id] ?? ''} onChange={e => setCityQuery(m => ({ ...m, [row.id]: e.target.value }))}
              placeholder="Buscar especialidade" iconLeft={<Search size={13} />} clearable wrapperClassName="w-60" />
          )}
        </div>
        {q && achadas.length === 0 && <p className="text-xs text-gray-500">Nenhuma especialidade com “{cityQuery[row.id]}”.</p>}
        <div className="flex flex-wrap gap-1.5">
          {visiveis.map(it => (
            <span key={it.id}
              title={it.providerNames.slice(0, 12).join('\n') + (it.providerNames.length > 12 ? `\n… e mais ${it.providerNames.length - 12}` : '')}
              className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs text-gray-700 ring-1 ring-gray-200">
              {it.specialty}
              <span className="rounded-full bg-violet-50 px-1.5 text-[11px] font-semibold text-violet-700">{it.providers}</span>
            </span>
          ))}
          {!todas && achadas.length > LIMITE && (
            <button type="button" onClick={() => setCityShowAll(ids => [...ids, row.id])}
              className="cursor-pointer rounded-full px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50">
              ver todas ({achadas.length})
            </button>
          )}
        </div>
      </div>
    )
  }

  // clique no mapa: resolve UF → estado cadastrado
  function onMapSelect(uf: string) {
    const st = states.find(s => s.abbreviation === uf)
    if (!st) { toast.info({ title: 'Estado sem cadastro', description: `Não há o estado "${uf}" cadastrado no sistema.` }); return }
    selectState(uf, st.id, st.state)
  }

  // filtro local (texto + cidade selecionada)
  const filtered = useMemo(() => {
    let r = cityRows
    if (cityFilter) {
      const cityName = cities.find(c => c.id === cityFilter)?.city
      if (cityName) {
        const cn = normalizeForMatch(cityName)
        r = r.filter(x => normalizeForMatch(x.name) === cn)
      }
    }
    const q = normalizeForMatch(search)
    if (q) r = r.filter(x => normalizeForMatch(x.name).includes(q))
    return r
  }, [cityRows, search, cityFilter, cities])

  const lastPage = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePage = Math.min(page, lastPage)
  const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize)
  const meta: PaginationMeta = {
    current_page: safePage, per_page: pageSize, last_page: lastPage, total: filtered.length,
    from: filtered.length ? (safePage - 1) * pageSize + 1 : null,
    to: Math.min(safePage * pageSize, filtered.length) || null,
  }

  const columns: DataTableColumn<MapRow>[] = [
    { key: 'name', header: 'Cidade', alwaysVisible: true, render: r => {
      const open = openCities.includes(r.id)
      return (
        <span className="inline-flex items-center gap-2">
          <button type="button" onClick={() => toggleCity(r)} aria-expanded={open}
            aria-label={open ? `Fechar especialidades de ${r.name}` : `Ver especialidades de ${r.name}`}
            title={open ? 'Fechar especialidades' : 'Ver especialidades'}
            className="inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-brand-600 ring-1 ring-brand-200 transition hover:bg-brand-50">
            {open ? <Minus size={14} /> : <Plus size={14} />}
          </button>
          <span className="font-medium text-gray-800 inline-flex items-center gap-1.5">
            <MapPin size={14} className="text-brand-500" />{r.name}
          </span>
        </span>
      )
    } },
    { key: 'providers', header: 'Credenciados (aceitos)', align: 'center', className: 'w-48', render: r => <Badge variant="green">{r.providers}</Badge> },
    { key: 'specialties', header: 'Especialidades', align: 'center', className: 'w-36', render: r => <span className="text-gray-600">{r.specialtiesCount}</span> },
    { key: 'tests', header: 'Exames', align: 'center', className: 'w-28', render: r => <span className="text-gray-600">{r.testsCount}</span> },
  ]

  const exp = useDataTableExport<MapRow>({
    filename: `mapa-credenciados-${selectedUf ?? 'estado'}`,
    service: { list: async () => ({ data: filtered }) },
    columns: [
      { header: 'Cidade', accessor: r => r.name },
      { header: 'Credenciados (aceitos)', accessor: r => String(r.providers) },
      { header: 'Especialidades', accessor: r => String(r.specialtiesCount) },
      { header: 'Exames', accessor: r => String(r.testsCount) },
    ],
  })

  const totalCredentialed = Object.values(stateCounts).reduce((a, b) => a + b, 0)

  // bignumbers do estado inteiro (independente do filtro de cidade da tabela)
  const stateTotals = useMemo(() => cityRows.reduce((acc, r) => ({
    providers: acc.providers + r.providers,
    specialties: acc.specialties + r.specialtiesCount,
    tests: acc.tests + r.testsCount,
  }), { providers: 0, specialties: 0, tests: 0 }), [cityRows])
  const citiesServed = cityRows.length
  const topCity = useMemo(
    () => cityRows.reduce<MapRow | null>((best, r) => (!best || r.providers > best.providers ? r : best), null),
    [cityRows],
  )

  const bignumbers = [
    { label: 'Credenciados aceitos', value: stateTotals.providers, icon: Stethoscope, color: 'text-emerald-600', ring: 'ring-emerald-200', bg: 'bg-emerald-50' },
    { label: 'Cidades atendidas',    value: citiesServed,          icon: Building2,   color: 'text-brand-600',   ring: 'ring-brand-200',   bg: 'bg-brand-50' },
    { label: 'Especialidades',       value: stateTotals.specialties, icon: Stethoscope, color: 'text-violet-600',  ring: 'ring-violet-200',  bg: 'bg-violet-50' },
    { label: 'Exames',               value: stateTotals.tests,     icon: FlaskConical, color: 'text-amber-600',   ring: 'ring-amber-200',   bg: 'bg-amber-50' },
  ]

  return (
    <>
      <PageHeader title="Mapa de credenciados" subtitle="Clique em um estado para ver os credenciados aceitos por cidade"
        crumbs={[{ label: 'Credenciamento' }, { label: 'Relatórios' }, { label: 'Mapa de credenciados' }]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Mapa */}
        <div className="lg:col-span-3 bg-white rounded-md ring-1 ring-gray-200 p-4 sm:p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
              <MapPinned size={16} className="text-brand-500" /> Brasil
            </div>
            <span className="text-xs text-gray-500">{totalCredentialed} credenciado(s) aceito(s)</span>
          </div>
          {loadingMap
            ? <div className="h-80 grid place-items-center text-sm text-gray-400">Carregando mapa</div>
            : <BrazilMap counts={stateCounts} selected={selectedUf} onSelect={onMapSelect} />}
        </div>

        {/* Painel do estado selecionado: bignumbers */}
        <div className="lg:col-span-2">
          {!selectedUf ? (
            <div className="h-full min-h-64 grid place-items-center bg-white rounded-md ring-1 ring-gray-200 p-6 text-center">
              <div>
                <MapPin size={28} className="mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">Selecione um estado no mapa para ver os indicadores e a lista de cidades.</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <MapPinned size={16} className="text-brand-500" />
                <h3 className="text-sm font-semibold text-gray-800">{selectedStateName}</h3>
              </div>
              <div className="flex flex-col gap-3">
                {bignumbers.map(b => (
                  <div key={b.label} className={`rounded-md ring-1 ${b.ring} ${b.bg} p-4 flex items-center justify-between`}>
                    <div>
                      <span className="text-[11px] font-medium text-gray-500 leading-tight">{b.label}</span>
                      <div className={`mt-1 text-2xl font-bold ${b.color}`}>{loadingCities ? '—' : b.value}</div>
                    </div>
                    <b.icon size={20} className={b.color} />
                  </div>
                ))}
              </div>
              {topCity && topCity.providers > 0 && (
                <div className="rounded-md ring-1 ring-gray-200 bg-white px-4 py-3">
                  <div className="text-[11px] font-medium text-gray-500">Cidade com mais credenciados</div>
                  <div className="mt-0.5 text-sm font-semibold text-gray-800 flex items-center justify-between">
                    <span className="break-words">{topCity.name}</span>
                    <Badge variant="green">{topCity.providers}</Badge>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tabela de cidades — largura total, abaixo do mapa/indicadores */}
      {selectedUf && (
        <div className="bg-white rounded-md ring-1 ring-gray-200 p-4 sm:p-5 mt-4">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
            <h3 className="text-sm font-semibold text-gray-800">Cidades de {selectedStateName}</h3>
            <div className="flex flex-wrap items-end gap-3">
              <Select
                label="Filtrar por cidade" value={cityFilter}
                onChange={(e) => { setCityFilter(e.target.value); setPage(1) }}
                placeholder="Todas as cidades"
                options={cities.map(c => ({ value: c.id, label: c.city }))}
                wrapperClassName="w-56"
              />
              <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                placeholder="Buscar cidade na lista" iconLeft={<Search size={14} />} clearable
                wrapperClassName="w-56" />
            </div>
          </div>

          <DataTable
            tableId="credentialing.report-map"
            columns={columns} rows={paged} loading={loadingCities} getRowKey={r => r.id}
            renderExpanded={renderCityDetail}
            emptyMessage="Nenhum credenciado aceito neste estado."
            exportable onExportPage={() => exp.exportPage(paged)} onExportAll={exp.exportAll}
          />
          <Pagination meta={meta} size={pageSize} onChange={setPage} onSizeChange={setPageSize} />
        </div>
      )}
    </>
  )
}
